// Closing a link that has already been mailed out.
//
// This is not a `window.confirm`, for one reason: the CRA reaching for it has just
// realised they sent something wrong, and the thing they most need to check is *whose*
// link they are about to close — the row they clicked may not be the row they meant. So
// the site and the address the mail went to are the first thing on the screen.
//
// The second thing is what this does and does not do. "회수" promises more than any web
// application can deliver: the mail is in somebody's inbox and stays there. Saying so
// here is cheaper than letting a CRA believe the message was pulled back.
import React, { useState } from "react";
import styled from "styled-components";
import {
  Backdrop, Modal, Header, Title, CloseBtn, Body, Footer, Btn, Warning,
} from "./emailModalUI";

// Narrower than the mail modals — this is a confirmation, not a workspace.
const Sheet = styled(Modal)`
  width: 480px;
`;

const Target = styled.div`
  margin-bottom: 12px;
  padding: 10px 12px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background: #f8f9fa;
  font-size: 13px;
  line-height: 1.7;
  color: #495057;
  b { color: #212529; }
`;

const Undo = styled.div`
  margin-bottom: 12px;
  font-size: 12px;
  line-height: 1.7;
  color: #6c757d;
  b { color: #495057; font-weight: 600; }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #495057;
`;

const Reason = styled.textarea`
  width: 100%;
  min-height: 68px;
  padding: 7px 9px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 13px;
  font-family: inherit;
  line-height: 1.5;
  resize: vertical;
  box-sizing: border-box;
  &:focus { outline: none; border-color: #0d6efd; }
`;

const Count = styled.div`
  margin-top: 3px;
  text-align: right;
  font-size: 11px;
  color: ${(p) => (p.$over ? "#dc3545" : "#adb5bd")};
`;

// Red, because the confirming button here closes something. Matches the 만료/회수
// vocabulary the grid already speaks (ExpiredDate, SendBtn $expired).
const DangerBtn = styled(Btn)`
  border-color: #b02a37;
  background: #b02a37;
  color: #fff;
`;

// SFQ_AuditTrail.comment is NVARCHAR(1000); the API clips past it rather than failing,
// but a silently truncated explanation is worse than one the CRA was told to shorten.
const REASON_MAX = 1000;

export default function RevokeLinkModal({ inv, onConfirm, onClose }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy]     = useState(false);

  const over = reason.length > REASON_MAX;
  const site = inv.siteName || inv.siteCode || `Invitation #${inv.id}`;

  async function submit() {
    if (over || busy) return;
    setBusy(true);
    try {
      await onConfirm(reason.trim() || null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Backdrop onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <Sheet>
        <Header>
          <Title>초대 링크 회수</Title>
          <CloseBtn onClick={onClose} disabled={busy}>×</CloseBtn>
        </Header>
        <Body>
          <Target>
            <div><b>{site}</b></div>
            <div>{inv.piName ? `${inv.piName} · ` : ""}{inv.piEmail}</div>
          </Target>

          <Warning>
            이미 발송된 <b>메일 자체는 회수되지 않습니다.</b> 수신자의 메일함에 그대로 남습니다.
            회수되는 것은 그 메일에 담긴 <b>설문 링크</b>로, 즉시 열리지 않게 되며 수신자에게는
            &ldquo;링크 유효기간이 지났습니다&rdquo; 화면이 표시됩니다.
          </Warning>

          <Undo>
            작성 중이던 답변은 <b>그대로 보존</b>됩니다. 다시 열어주려면 <b>Edit</b>에서 만료일을
            미루거나(같은 비밀번호가 계속 유효합니다), <b>Resend</b>로 새 메일을 보내면 됩니다.
            <br />
            사이트에는 별도의 안내 메일이 <b>발송되지 않습니다.</b>
          </Undo>

          <Label htmlFor="revoke-reason">회수 사유 (선택)</Label>
          <Reason
            id="revoke-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="예: PI 이메일 주소를 잘못 입력하여 재발송 예정"
            disabled={busy}
          />
          <Count $over={over}>
            {reason.length} / {REASON_MAX}
          </Count>
        </Body>
        <Footer>
          <Btn onClick={onClose} disabled={busy}>취소</Btn>
          <DangerBtn onClick={submit} disabled={busy || over}>
            {busy ? "회수 중..." : "링크 회수"}
          </DangerBtn>
        </Footer>
      </Sheet>
    </Backdrop>
  );
}
